import { SimpleLegalFooter } from "@/components/LegalFooter";
import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { Progress } from "@/components/ui/progress";
import {
  Car,
  Bike,
  User,
  FileText,
  Check,
  Search,
  CheckCircle2,
  Phone,
  Send,
  ChevronLeft,
  ChevronRight,
  Shield,
  MapPin,
  Calendar,
  Gauge,
  History,
  Settings,
  Zap,
  CreditCard,
  Home,
  Briefcase,
  Building,
  AlertCircle,
  Star,
  Plus,
  Minus,
  Sparkles,
  Clock,
  TrendingDown,
  Award,
  Info,
  Loader2,
  ArrowRight,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { HeaderLogo } from "@/components/AnimatedLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Logo JYVIS
const JYVIS_LOGO = "/logo.svg";

// Logo TheGoodDeal.fr
const THEGOODDEAL_LOGO = "/images/thegooddeal.jpg";

// Contact Info
const contactInfo = {
  phone: "01 48 20 98 27",
  whatsapp: "https://wa.me/message/XE3IM2IGGU5VE1",
};

// Marques populaires avec logos
const CAR_BRANDS = [
  { name: "Renault", popular: true },
  { name: "Peugeot", popular: true },
  { name: "Citroën", popular: true },
  { name: "Volkswagen", popular: true },
  { name: "Toyota", popular: true },
  { name: "BMW", popular: false },
  { name: "Mercedes", popular: false },
  { name: "Audi", popular: false },
  { name: "Ford", popular: false },
  { name: "Dacia", popular: true },
  { name: "Fiat", popular: false },
  { name: "Honda", popular: false },
  { name: "Hyundai", popular: false },
  { name: "Kia", popular: false },
  { name: "Nissan", popular: false },
  { name: "Opel", popular: false },
  { name: "Seat", popular: false },
  { name: "Skoda", popular: false },
  { name: "Volvo", popular: false },
  { name: "Autre", popular: false },
];

const MOTO_BRANDS = [
  { name: "Yamaha", popular: true },
  { name: "Honda", popular: true },
  { name: "Kawasaki", popular: true },
  { name: "Suzuki", popular: true },
  { name: "BMW", popular: false },
  { name: "Ducati", popular: false },
  { name: "Harley-Davidson", popular: false },
  { name: "KTM", popular: false },
  { name: "Triumph", popular: false },
  { name: "Autre", popular: false },
];

// Types de carrosserie avec icônes
const BODY_TYPES = {
  auto: [
    { name: "Berline", icon: "🚗" },
    { name: "SUV", icon: "🚙" },
    { name: "Break", icon: "🚐" },
    { name: "Citadine", icon: "🚘" },
    { name: "Coupé", icon: "🏎️" },
    { name: "Monospace", icon: "🚐" },
  ],
  moto: [
    { name: "Roadster", icon: "🏍️" },
    { name: "Sportive", icon: "🏍️" },
    { name: "Trail", icon: "🏍️" },
    { name: "Scooter", icon: "🛵" },
    { name: "Custom", icon: "🏍️" },
  ],
};

// Énergies
const FUEL_TYPES = [
  { name: "Essence", icon: "⛽", color: "text-amber-600" },
  { name: "Diesel", icon: "🛢️", color: "text-gray-600" },
  { name: "Hybride", icon: "🔋", color: "text-emerald-600" },
  { name: "Électrique", icon: "⚡", color: "text-blue-600" },
];

// Professions courantes
const PROFESSIONS = [
  "Salarié(e)",
  "Cadre",
  "Fonctionnaire",
  "Artisan/Commerçant",
  "Profession libérale",
  "Étudiant(e)",
  "Retraité(e)",
  "Sans emploi",
  "Autre",
];

export default function DevisCompletV2() {
  const navigate = useNavigate();
  const { type: urlType } = useParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [vehicleType, setVehicleType] = useState(urlType === "moto" ? "moto" : "auto");
  const [loading, setLoading] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [progress, setProgress] = useState(0);

  // ==================== FORM DATA avec valeurs par défaut intelligentes ====================
  
  const [vehicleData, setVehicleData] = useState({
    plaque: "",
    marque: "",
    modele: "",
    version: "",
    energie: "Essence",
    puissanceFiscale: "6",
    boiteVitesse: "manuelle",
    dateMiseCirculation: "",
    valeurVehicule: "",
    nombrePortes: "5",
    typeCarrosserie: "",
  });

  const [situationData, setSituationData] = useState({
    vehiculeNeuf: "occasion",
    dateAchat: "",
    modeAchat: "comptant",
    proprietaire: "personnel",
  });

  const [usageData, setUsageData] = useState({
    usage: "domicile_travail",
    kilometrageAnnuel: 12000,
    stationnement: "garage",
  });

  const [conducteurData, setConducteurData] = useState({
    civilite: "M",
    nom: "",
    prenom: "",
    dateNaissance: "",
    datePermis: "",
    profession: "",
    telephone: "",
    email: "",
    codePostal: "",
    hasSecondConducteur: false,
  });

  const [historiqueData, setHistoriqueData] = useState({
    bonus: 1.00,
    nbAccidents: 0,
    nbSinistres: 0,
    assureActuellement: "oui",
    ancienAssureur: "",
  });

  const [formuleData, setFormuleData] = useState({
    formule: "tiers_plus",
    options: {
      brisGlace: true,
      vol: false,
      incendie: false,
      assistance: true,
      vehiculeRemplacement: false,
      protectionJuridique: false,
    }
  });

  const [vehicleInfo, setVehicleInfo] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [estimatedBonus, setEstimatedBonus] = useState(null);

  // ==================== AUTO-FILL INTELLIGENT ====================

  // Calculer le bonus estimé basé sur la date du permis
  useEffect(() => {
    if (conducteurData.datePermis) {
      const permisDate = new Date(conducteurData.datePermis);
      const now = new Date();
      const yearsWithLicense = (now - permisDate) / (1000 * 60 * 60 * 24 * 365);
      
      // Calcul simplifié du bonus estimé
      // Chaque année sans accident = -5% de bonus (jusqu'à 50%)
      let estimated = Math.max(0.50, 1.00 - (yearsWithLicense * 0.05));
      estimated = Math.round(estimated * 100) / 100;
      
      setEstimatedBonus(estimated);
      
      // Suggérer le bonus si l'utilisateur n'a pas encore modifié
      if (historiqueData.bonus === 1.00 && yearsWithLicense > 2) {
        setHistoriqueData(prev => ({ ...prev, bonus: estimated }));
      }
    }
  }, [conducteurData.datePermis]);

  // Auto-ajuster le kilométrage selon l'usage
  useEffect(() => {
    const kmByUsage = {
      prive: 8000,
      domicile_travail: 15000,
      professionnel: 25000,
    };
    setUsageData(prev => ({ ...prev, kilometrageAnnuel: kmByUsage[prev.usage] || 15000 }));
  }, [usageData.usage]);

  // Calculer la progression
  useEffect(() => {
    let filled = 0;
    let total = 0;
    
    // Step 1
    total += 3;
    if (vehicleData.marque) filled++;
    if (vehicleData.modele) filled++;
    if (vehicleData.energie) filled++;
    
    // Step 2
    total += 2;
    if (situationData.vehiculeNeuf) filled++;
    if (situationData.modeAchat) filled++;
    
    // Step 3
    total += 2;
    if (usageData.usage) filled++;
    if (usageData.stationnement) filled++;
    
    // Step 4
    total += 4;
    if (conducteurData.nom) filled++;
    if (conducteurData.prenom) filled++;
    if (conducteurData.telephone) filled++;
    if (conducteurData.email) filled++;
    
    // Step 5
    total += 1;
    filled++; // Bonus toujours rempli par défaut
    
    setProgress(Math.round((filled / total) * 100));
  }, [vehicleData, situationData, usageData, conducteurData, historiqueData]);

  // ==================== LOOKUP PLAQUE AMÉLIORÉ ====================
  
  const lookupPlate = async () => {
    const plaque = vehicleData.plaque.replace(/[\s-]/g, '').toUpperCase();
    if (plaque.length < 7) {
      toast.error("Format: AB-123-CD ou AB123CD");
      return;
    }
    
    setLookupLoading(true);
    try {
      const response = await axios.get(`${API}/vehicle/lookup/${plaque}`);
      const data = response.data;
      setVehicleInfo(data);
      
      // Remplissage automatique avec animation
      setTimeout(() => {
        setVehicleData(prev => ({
          ...prev,
          marque: data.marque || prev.marque,
          modele: data.modele || prev.modele,
          energie: data.energie || prev.energie,
          puissanceFiscale: data.puissance_fiscale?.toString() || prev.puissanceFiscale,
          dateMiseCirculation: data.date_mise_circulation || prev.dateMiseCirculation,
        }));
        toast.success(`✨ ${data.marque} ${data.modele} identifié !`);
      }, 500);
      
    } catch (error) {
      toast.error("Véhicule non trouvé. Remplissez manuellement.");
    }
    setLookupLoading(false);
  };

  // Format plaque en temps réel
  const handlePlaqueChange = (value) => {
    let formatted = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (formatted.length > 2 && formatted.length <= 5) {
      formatted = formatted.slice(0, 2) + '-' + formatted.slice(2);
    } else if (formatted.length > 5) {
      formatted = formatted.slice(0, 2) + '-' + formatted.slice(2, 5) + '-' + formatted.slice(5, 7);
    }
    setVehicleData({ ...vehicleData, plaque: formatted.slice(0, 9) });
  };

  // ==================== NAVIGATION ====================
  
  const nextStep = () => {
    if (currentStep < 6) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const goToStep = (step) => {
    if (step <= currentStep || step === currentStep + 1) {
      setCurrentStep(step);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // ==================== SUBMIT ====================
  
  const handleSubmit = async () => {
    if (!accepted) {
      toast.error("Veuillez accepter les conditions");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        type: vehicleType,
        vehicule: vehicleData,
        situation: situationData,
        usage: usageData,
        conducteur: conducteurData,
        historique: historiqueData,
        formule: formuleData,
        timestamp: new Date().toISOString(),
      };

      await axios.post(`${API}/devis/complet`, payload);
      setSubmitted(true);
      toast.success("Demande envoyée ! 🎉");
    } catch (error) {
      toast.success("Demande enregistrée ! Nous vous rappelons rapidement.");
      setSubmitted(true);
    }
    setLoading(false);
  };

  // ==================== BONUS HELPERS ====================
  
  const bonusValues = [0.50, 0.52, 0.54, 0.56, 0.58, 0.60, 0.62, 0.64, 0.66, 0.68, 
    0.70, 0.72, 0.74, 0.76, 0.78, 0.80, 0.82, 0.84, 0.86, 0.88, 0.90, 0.92, 0.94, 
    0.96, 0.98, 1.00, 1.25, 1.50, 2.00, 2.50, 3.00, 3.50];

  const getBonusDisplay = (value) => {
    if (value <= 0.50) return { label: "Bonus maximum 🏆", color: "text-emerald-600", bg: "bg-emerald-50" };
    if (value < 0.70) return { label: `Excellent bonus`, color: "text-emerald-500", bg: "bg-emerald-50" };
    if (value < 1.00) return { label: `Bon bonus`, color: "text-blue-500", bg: "bg-blue-50" };
    if (value === 1.00) return { label: "Neutre", color: "text-gray-600", bg: "bg-gray-50" };
    return { label: "Malus", color: "text-red-500", bg: "bg-red-50" };
  };

  // ==================== RENDER SUCCESS ====================
  
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#102692] via-[#1a3bc7] to-[#102692] flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-3xl p-8 max-w-lg w-full text-center shadow-2xl"
        >
          <motion.div 
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring" }}
            className="w-24 h-24 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <CheckCircle2 className="w-12 h-12 text-white" />
          </motion.div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Merci {conducteurData.prenom} ! 🎉</h2>
          <p className="text-gray-600 mb-6">
            Votre demande de devis {vehicleType === "auto" ? "auto" : "moto"} est enregistrée.
            <br />Un conseiller vous contacte <strong>dans les 30 minutes</strong>.
          </p>
          
          <div className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-xl p-5 mb-6">
            <p className="text-sm text-gray-600 mb-3">🚀 Besoin d'une réponse immédiate ?</p>
            <a 
              href={contactInfo.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#25D366] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#20BA5A] transition-all hover:scale-105"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Discuter sur WhatsApp
            </a>
          </div>
          
          <Link to="/">
            <Button variant="outline" className="w-full">Retour à l'accueil</Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  // ==================== STEPS CONFIG ====================
  
  const steps = [
    { id: 1, title: "Véhicule", shortTitle: "1", icon: vehicleType === "auto" ? Car : Bike },
    { id: 2, title: "Achat", shortTitle: "2", icon: CreditCard },
    { id: 3, title: "Usage", shortTitle: "3", icon: Gauge },
    { id: 4, title: "Vous", shortTitle: "4", icon: User },
    { id: 5, title: "Bonus", shortTitle: "5", icon: Award },
    { id: 6, title: "Formule", shortTitle: "6", icon: Shield },
  ];

  // ==================== RENDER FORM ====================
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header simplifié */}
      <header className="bg-white/80 backdrop-blur-sm border-b sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <HeaderLogo theme="contact" size="sm" showName={false} dark={false} />
          </Link>
          <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#102692]">
            <Phone className="w-4 h-4" />
            <span className="hidden sm:inline">{contactInfo.phone}</span>
          </a>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Type selector - Plus visible */}
        <div className="flex justify-center mb-6">
          <div className="bg-white rounded-2xl p-1.5 shadow-lg inline-flex gap-1">
            <button
              onClick={() => setVehicleType("auto")}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
                vehicleType === "auto"
                  ? "bg-[#102692] text-white shadow-md"
                  : "text-gray-500 hover:bg-gray-100"
              }`}
            >
              <Car className="w-5 h-5" />
              Voiture
            </button>
            <button
              onClick={() => setVehicleType("moto")}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
                vehicleType === "moto"
                  ? "bg-[#102692] text-white shadow-md"
                  : "text-gray-500 hover:bg-gray-100"
              }`}
            >
              <Bike className="w-5 h-5" />
              Moto
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-500">Progression</span>
            <span className="text-sm font-medium text-[#102692]">{progress}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Steps indicators - Cliquables */}
        <div className="flex justify-between mb-8 relative">
          <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-200 -z-10">
            <div 
              className="h-full bg-[#102692] transition-all duration-500"
              style={{ width: `${((currentStep - 1) / 5) * 100}%` }}
            />
          </div>
          
          {steps.map((step) => (
            <button
              key={step.id}
              onClick={() => goToStep(step.id)}
              disabled={step.id > currentStep + 1}
              className={`flex flex-col items-center transition-all ${
                step.id <= currentStep ? "cursor-pointer" : step.id === currentStep + 1 ? "cursor-pointer" : "cursor-not-allowed opacity-50"
              }`}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 transition-all ${
                step.id < currentStep
                  ? "bg-emerald-500 text-white"
                  : step.id === currentStep
                  ? "bg-[#102692] text-white ring-4 ring-[#102692]/20"
                  : "bg-gray-200 text-gray-500"
              }`}>
                {step.id < currentStep ? <Check className="w-5 h-5" /> : <step.icon className="w-5 h-5" />}
              </div>
              <span className={`text-xs font-medium ${step.id === currentStep ? "text-[#102692]" : "text-gray-500"}`}>
                {step.title}
              </span>
            </button>
          ))}
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
          <AnimatePresence mode="wait">
            
            {/* ==================== STEP 1: VÉHICULE ==================== */}
            {currentStep === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="p-6"
              >
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[#102692]/10 rounded-2xl mb-3">
                    {vehicleType === "auto" ? <Car className="w-8 h-8 text-[#102692]" /> : <Bike className="w-8 h-8 text-[#102692]" />}
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Quelle {vehicleType === "auto" ? "voiture" : "moto"} assurez-vous ?</h2>
                  <p className="text-gray-500 text-sm mt-1">Entrez la plaque pour un remplissage automatique ✨</p>
                </div>

                {/* Recherche plaque - Mise en avant */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-5 mb-6">
                  <Label className="text-sm font-medium mb-2 block flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#102692]" />
                    Remplissage automatique
                  </Label>
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <Input
                        placeholder="AB-123-CD"
                        value={vehicleData.plaque}
                        onChange={(e) => handlePlaqueChange(e.target.value)}
                        className="text-center text-lg font-mono tracking-wider h-12 bg-white"
                        onKeyPress={(e) => e.key === 'Enter' && lookupPlate()}
                      />
                    </div>
                    <Button 
                      onClick={lookupPlate} 
                      disabled={lookupLoading}
                      className="h-12 px-6 bg-[#102692]"
                    >
                      {lookupLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
                    </Button>
                  </div>
                  {vehicleInfo && (
                    <motion.div 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-3 p-3 bg-white rounded-xl flex items-center gap-3"
                    >
                      <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                      <span className="font-medium">{vehicleInfo.marque} {vehicleInfo.modele}</span>
                      <span className="text-xs text-gray-500 ml-auto">Rempli automatiquement</span>
                    </motion.div>
                  )}
                </div>

                {/* Marque - Boutons populaires */}
                <div className="mb-5">
                  <Label className="mb-3 block">Marque *</Label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {(vehicleType === "auto" ? CAR_BRANDS : MOTO_BRANDS)
                      .filter(b => b.popular)
                      .map((brand) => (
                        <button
                          key={brand.name}
                          onClick={() => setVehicleData({ ...vehicleData, marque: brand.name })}
                          className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                            vehicleData.marque === brand.name
                              ? "border-[#102692] bg-[#102692]/5 text-[#102692]"
                              : "border-gray-200 text-gray-600 hover:border-gray-300"
                          }`}
                        >
                          {brand.name}
                        </button>
                      ))}
                    <Select
                      value={vehicleData.marque}
                      onValueChange={(v) => setVehicleData({ ...vehicleData, marque: v })}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder="Autre..." />
                      </SelectTrigger>
                      <SelectContent>
                        {(vehicleType === "auto" ? CAR_BRANDS : MOTO_BRANDS).map((brand) => (
                          <SelectItem key={brand.name} value={brand.name}>{brand.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Modèle */}
                <div className="mb-5">
                  <Label>Modèle *</Label>
                  <Input
                    placeholder={vehicleType === "auto" ? "Ex: 308, Clio, Golf..." : "Ex: MT-07, CB650R..."}
                    value={vehicleData.modele}
                    onChange={(e) => setVehicleData({ ...vehicleData, modele: e.target.value })}
                    className="h-12"
                  />
                </div>

                {/* Énergie - Boutons visuels */}
                <div className="mb-5">
                  <Label className="mb-3 block">Énergie *</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {FUEL_TYPES.map((fuel) => (
                      <button
                        key={fuel.name}
                        onClick={() => setVehicleData({ ...vehicleData, energie: fuel.name })}
                        className={`p-3 rounded-xl border-2 text-center transition-all ${
                          vehicleData.energie === fuel.name
                            ? "border-[#102692] bg-[#102692]/5"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <span className="text-2xl block mb-1">{fuel.icon}</span>
                        <span className={`text-xs font-medium ${vehicleData.energie === fuel.name ? "text-[#102692]" : "text-gray-600"}`}>
                          {fuel.name}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Puissance et Date */}
                <div className="grid grid-cols-2 gap-4 mb-5">
                  <div>
                    <Label>Puissance (CV)</Label>
                    <Select
                      value={vehicleData.puissanceFiscale}
                      onValueChange={(v) => setVehicleData({ ...vehicleData, puissanceFiscale: v })}
                    >
                      <SelectTrigger className="h-12">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20].map((cv) => (
                          <SelectItem key={cv} value={cv.toString()}>{cv} CV</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Mise en circulation</Label>
                    <Input
                      type="date"
                      value={vehicleData.dateMiseCirculation}
                      onChange={(e) => setVehicleData({ ...vehicleData, dateMiseCirculation: e.target.value })}
                      className="h-12"
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {/* ==================== STEP 2: SITUATION ==================== */}
            {currentStep === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="p-6"
              >
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[#102692]/10 rounded-2xl mb-3">
                    <CreditCard className="w-8 h-8 text-[#102692]" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Comment l'avez-vous acheté ?</h2>
                </div>

                {/* Neuf / Occasion */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  {[
                    { value: "neuf", label: "Neuf", icon: Star, desc: "0 km" },
                    { value: "occasion", label: "Occasion", icon: Clock, desc: "Second main" },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setSituationData({ ...situationData, vehiculeNeuf: opt.value })}
                      className={`p-5 rounded-2xl border-2 text-center transition-all ${
                        situationData.vehiculeNeuf === opt.value
                          ? "border-[#102692] bg-[#102692]/5"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <opt.icon className={`w-8 h-8 mx-auto mb-2 ${situationData.vehiculeNeuf === opt.value ? "text-[#102692]" : "text-gray-400"}`} />
                      <p className="font-semibold">{opt.label}</p>
                      <p className="text-xs text-gray-500">{opt.desc}</p>
                    </button>
                  ))}
                </div>

                {/* Mode d'achat */}
                <div className="mb-6">
                  <Label className="mb-3 block">Mode de financement</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { value: "comptant", label: "Comptant", icon: "💵" },
                      { value: "credit", label: "Crédit", icon: "🏦" },
                      { value: "leasing", label: "LOA/LLD", icon: "📝" },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setSituationData({ ...situationData, modeAchat: opt.value })}
                        className={`p-4 rounded-xl border-2 text-center transition-all ${
                          situationData.modeAchat === opt.value
                            ? "border-[#102692] bg-[#102692]/5"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <span className="text-2xl block mb-1">{opt.icon}</span>
                        <span className="text-sm font-medium">{opt.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Propriétaire */}
                <div>
                  <Label className="mb-3 block">Propriétaire</Label>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { value: "personnel", label: "Personnel", icon: User },
                      { value: "societe", label: "Société", icon: Building },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setSituationData({ ...situationData, proprietaire: opt.value })}
                        className={`p-4 rounded-xl border-2 flex items-center gap-3 transition-all ${
                          situationData.proprietaire === opt.value
                            ? "border-[#102692] bg-[#102692]/5"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <opt.icon className={`w-6 h-6 ${situationData.proprietaire === opt.value ? "text-[#102692]" : "text-gray-400"}`} />
                        <span className="font-medium">{opt.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* ==================== STEP 3: UTILISATION ==================== */}
            {currentStep === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="p-6"
              >
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[#102692]/10 rounded-2xl mb-3">
                    <Gauge className="w-8 h-8 text-[#102692]" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Comment utilisez-vous votre véhicule ?</h2>
                </div>

                {/* Usage */}
                <div className="mb-6">
                  <Label className="mb-3 block">Usage principal *</Label>
                  <div className="space-y-3">
                    {[
                      { value: "prive", label: "Privé / Loisirs", desc: "Week-ends, vacances", icon: Home, km: "~8 000 km/an" },
                      { value: "domicile_travail", label: "Domicile-Travail", desc: "Trajets quotidiens", icon: Briefcase, km: "~15 000 km/an" },
                      { value: "professionnel", label: "Professionnel", desc: "Rendez-vous clients", icon: Building, km: "~25 000 km/an" },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setUsageData({ ...usageData, usage: opt.value })}
                        className={`w-full p-4 rounded-xl border-2 flex items-center gap-4 transition-all text-left ${
                          usageData.usage === opt.value
                            ? "border-[#102692] bg-[#102692]/5"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          usageData.usage === opt.value ? "bg-[#102692] text-white" : "bg-gray-100 text-gray-400"
                        }`}>
                          <opt.icon className="w-6 h-6" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold">{opt.label}</p>
                          <p className="text-sm text-gray-500">{opt.desc}</p>
                        </div>
                        <span className="text-xs text-gray-400">{opt.km}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Kilométrage - Slider visuel */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <Label>Kilométrage annuel estimé</Label>
                    <span className="text-2xl font-bold text-[#102692]">{usageData.kilometrageAnnuel.toLocaleString()} km</span>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <Slider
                      value={[usageData.kilometrageAnnuel]}
                      onValueChange={([v]) => setUsageData({ ...usageData, kilometrageAnnuel: v })}
                      min={5000}
                      max={40000}
                      step={1000}
                    />
                    <div className="flex justify-between mt-2 text-xs text-gray-400">
                      <span>5 000 km</span>
                      <span>40 000 km</span>
                    </div>
                  </div>
                </div>

                {/* Stationnement */}
                <div>
                  <Label className="mb-3 block">Où dort votre véhicule ? *</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { value: "garage", label: "Garage", icon: "🏠", risk: "Risque faible", color: "text-emerald-600" },
                      { value: "parking", label: "Parking", icon: "🅿️", risk: "Risque moyen", color: "text-amber-600" },
                      { value: "rue", label: "Rue", icon: "🛣️", risk: "Risque élevé", color: "text-red-500" },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setUsageData({ ...usageData, stationnement: opt.value })}
                        className={`p-4 rounded-xl border-2 text-center transition-all ${
                          usageData.stationnement === opt.value
                            ? "border-[#102692] bg-[#102692]/5"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <span className="text-3xl block mb-2">{opt.icon}</span>
                        <p className="font-medium text-sm">{opt.label}</p>
                        <p className={`text-xs ${opt.color}`}>{opt.risk}</p>
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* ==================== STEP 4: CONDUCTEUR ==================== */}
            {currentStep === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="p-6"
              >
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[#102692]/10 rounded-2xl mb-3">
                    <User className="w-8 h-8 text-[#102692]" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Qui êtes-vous ?</h2>
                  <p className="text-gray-500 text-sm">Pour personnaliser votre devis</p>
                </div>

                {/* Civilité */}
                <div className="flex gap-3 mb-5 justify-center">
                  {[
                    { value: "M", label: "Monsieur" },
                    { value: "Mme", label: "Madame" },
                  ].map((c) => (
                    <button
                      key={c.value}
                      onClick={() => setConducteurData({ ...conducteurData, civilite: c.value })}
                      className={`px-6 py-2 rounded-full border-2 font-medium transition-all ${
                        conducteurData.civilite === c.value
                          ? "border-[#102692] bg-[#102692] text-white"
                          : "border-gray-200 text-gray-600 hover:border-gray-300"
                      }`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>

                {/* Nom Prénom */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label>Prénom *</Label>
                    <Input
                      placeholder="Jean"
                      value={conducteurData.prenom}
                      onChange={(e) => setConducteurData({ ...conducteurData, prenom: e.target.value })}
                      className="h-12"
                    />
                  </div>
                  <div>
                    <Label>Nom *</Label>
                    <Input
                      placeholder="Dupont"
                      value={conducteurData.nom}
                      onChange={(e) => setConducteurData({ ...conducteurData, nom: e.target.value })}
                      className="h-12"
                    />
                  </div>
                </div>

                {/* Dates */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label>Date de naissance *</Label>
                    <Input
                      type="date"
                      value={conducteurData.dateNaissance}
                      onChange={(e) => setConducteurData({ ...conducteurData, dateNaissance: e.target.value })}
                      className="h-12"
                    />
                  </div>
                  <div>
                    <Label>Date du permis *</Label>
                    <Input
                      type="date"
                      value={conducteurData.datePermis}
                      onChange={(e) => setConducteurData({ ...conducteurData, datePermis: e.target.value })}
                      className="h-12"
                    />
                  </div>
                </div>

                {/* Contact */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label>Téléphone *</Label>
                    <Input
                      type="tel"
                      placeholder="06 12 34 56 78"
                      value={conducteurData.telephone}
                      onChange={(e) => setConducteurData({ ...conducteurData, telephone: e.target.value })}
                      className="h-12"
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      placeholder="jean@email.com"
                      value={conducteurData.email}
                      onChange={(e) => setConducteurData({ ...conducteurData, email: e.target.value })}
                      className="h-12"
                    />
                  </div>
                </div>

                {/* Code postal */}
                <div className="mb-4">
                  <Label>Code postal *</Label>
                  <Input
                    placeholder="75001"
                    value={conducteurData.codePostal}
                    onChange={(e) => setConducteurData({ ...conducteurData, codePostal: e.target.value })}
                    className="h-12 max-w-[200px]"
                    maxLength={5}
                  />
                </div>

                {/* Profession */}
                <div>
                  <Label>Profession</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {PROFESSIONS.slice(0, 5).map((prof) => (
                      <button
                        key={prof}
                        onClick={() => setConducteurData({ ...conducteurData, profession: prof })}
                        className={`px-3 py-1.5 rounded-lg border text-sm transition-all ${
                          conducteurData.profession === prof
                            ? "border-[#102692] bg-[#102692]/5 text-[#102692]"
                            : "border-gray-200 text-gray-600 hover:border-gray-300"
                        }`}
                      >
                        {prof}
                      </button>
                    ))}
                    <Select
                      value={conducteurData.profession}
                      onValueChange={(v) => setConducteurData({ ...conducteurData, profession: v })}
                    >
                      <SelectTrigger className="w-24 h-8">
                        <SelectValue placeholder="Autre" />
                      </SelectTrigger>
                      <SelectContent>
                        {PROFESSIONS.map((prof) => (
                          <SelectItem key={prof} value={prof}>{prof}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </motion.div>
            )}

            {/* ==================== STEP 5: BONUS ==================== */}
            {currentStep === 5 && (
              <motion.div
                key="step5"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="p-6"
              >
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[#102692]/10 rounded-2xl mb-3">
                    <Award className="w-8 h-8 text-[#102692]" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Votre bonus actuel</h2>
                  <p className="text-gray-500 text-sm">Indiqué sur votre relevé d'information</p>
                </div>

                {/* Bonus Display */}
                <div className={`rounded-2xl p-6 mb-6 ${getBonusDisplay(historiqueData.bonus).bg}`}>
                  <div className="text-center mb-4">
                    <span className={`text-5xl font-bold ${getBonusDisplay(historiqueData.bonus).color}`}>
                      {historiqueData.bonus.toFixed(2)}
                    </span>
                    <p className={`text-lg font-medium ${getBonusDisplay(historiqueData.bonus).color}`}>
                      {getBonusDisplay(historiqueData.bonus).label}
                    </p>
                  </div>
                  
                  <Slider
                    value={[bonusValues.indexOf(historiqueData.bonus) !== -1 ? bonusValues.indexOf(historiqueData.bonus) : 25]}
                    onValueChange={([idx]) => setHistoriqueData({ ...historiqueData, bonus: bonusValues[idx] })}
                    min={0}
                    max={bonusValues.length - 1}
                    step={1}
                    className="mb-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span className="text-emerald-600 font-medium">0.50 (max)</span>
                    <span>1.00</span>
                    <span className="text-red-500">Malus</span>
                  </div>
                </div>

                {/* Estimation automatique */}
                {estimatedBonus && estimatedBonus < 1.00 && conducteurData.datePermis && (
                  <div className="bg-blue-50 rounded-xl p-4 mb-6 flex items-center gap-3">
                    <Sparkles className="w-5 h-5 text-blue-500" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-blue-800">Estimation basée sur votre permis</p>
                      <p className="text-xs text-blue-600">Bonus estimé : {estimatedBonus.toFixed(2)}</p>
                    </div>
                    <button
                      onClick={() => setHistoriqueData({ ...historiqueData, bonus: estimatedBonus })}
                      className="text-xs bg-blue-500 text-white px-3 py-1 rounded-lg hover:bg-blue-600"
                    >
                      Appliquer
                    </button>
                  </div>
                )}

                {/* Raccourcis bonus */}
                <div className="mb-6">
                  <Label className="mb-2 block text-sm">Raccourcis</Label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { value: 0.50, label: "Max (0.50)" },
                      { value: 0.70, label: "Bon (0.70)" },
                      { value: 0.90, label: "Moyen (0.90)" },
                      { value: 1.00, label: "Neutre (1.00)" },
                    ].map((b) => (
                      <button
                        key={b.value}
                        onClick={() => setHistoriqueData({ ...historiqueData, bonus: b.value })}
                        className={`px-3 py-1.5 rounded-lg border text-sm transition-all ${
                          historiqueData.bonus === b.value
                            ? "border-[#102692] bg-[#102692]/10 text-[#102692]"
                            : "border-gray-200 text-gray-600 hover:border-gray-300"
                        }`}
                      >
                        {b.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Sinistres */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-xl p-4">
                    <Label className="mb-3 block text-center">Accidents (3 ans)</Label>
                    <div className="flex items-center justify-center gap-4">
                      <button
                        onClick={() => setHistoriqueData({ ...historiqueData, nbAccidents: Math.max(0, historiqueData.nbAccidents - 1) })}
                        className="w-10 h-10 rounded-full bg-white border flex items-center justify-center hover:bg-gray-100"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="text-3xl font-bold w-8 text-center">{historiqueData.nbAccidents}</span>
                      <button
                        onClick={() => setHistoriqueData({ ...historiqueData, nbAccidents: historiqueData.nbAccidents + 1 })}
                        className="w-10 h-10 rounded-full bg-white border flex items-center justify-center hover:bg-gray-100"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <Label className="mb-3 block text-center">Sinistres (5 ans)</Label>
                    <div className="flex items-center justify-center gap-4">
                      <button
                        onClick={() => setHistoriqueData({ ...historiqueData, nbSinistres: Math.max(0, historiqueData.nbSinistres - 1) })}
                        className="w-10 h-10 rounded-full bg-white border flex items-center justify-center hover:bg-gray-100"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="text-3xl font-bold w-8 text-center">{historiqueData.nbSinistres}</span>
                      <button
                        onClick={() => setHistoriqueData({ ...historiqueData, nbSinistres: historiqueData.nbSinistres + 1 })}
                        className="w-10 h-10 rounded-full bg-white border flex items-center justify-center hover:bg-gray-100"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* ==================== STEP 6: FORMULE ==================== */}
            {currentStep === 6 && (
              <motion.div
                key="step6"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="p-6"
              >
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[#102692]/10 rounded-2xl mb-3">
                    <Shield className="w-8 h-8 text-[#102692]" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Choisissez votre protection</h2>
                </div>

                {/* Formules */}
                <div className="space-y-3 mb-6">
                  {[
                    { 
                      value: "tiers", 
                      label: "Au Tiers", 
                      price: "15€",
                      desc: "Minimum légal",
                      includes: ["Responsabilité civile", "Défense pénale"],
                      color: "border-gray-300"
                    },
                    { 
                      value: "tiers_plus", 
                      label: "Tiers +", 
                      price: "25€",
                      desc: "Le plus populaire",
                      includes: ["Tiers", "Vol", "Incendie", "Bris de glace"],
                      popular: true,
                      color: "border-[#102692]"
                    },
                    { 
                      value: "tous_risques", 
                      label: "Tous Risques", 
                      price: "40€",
                      desc: "Protection maximale",
                      includes: ["Tiers+", "Tous dommages", "Assistance 0km"],
                      color: "border-emerald-500"
                    },
                  ].map((f) => (
                    <button
                      key={f.value}
                      onClick={() => setFormuleData({ ...formuleData, formule: f.value })}
                      className={`w-full p-4 rounded-xl border-2 text-left transition-all relative ${
                        formuleData.formule === f.value
                          ? `${f.color} bg-gradient-to-r from-white to-gray-50`
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      {f.popular && (
                        <span className="absolute -top-2 right-4 bg-[#F97316] text-white text-xs px-2 py-0.5 rounded-full">
                          Recommandé
                        </span>
                      )}
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-bold text-lg">{f.label}</p>
                          <p className="text-sm text-gray-500">{f.desc}</p>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {f.includes.map((item, i) => (
                              <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                                {item}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-[#102692]">{f.price}</p>
                          <p className="text-xs text-gray-400">/mois</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Options rapides */}
                <div className="mb-6">
                  <Label className="mb-3 block">Options recommandées</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { key: "brisGlace", label: "Bris de glace", icon: "🪟" },
                      { key: "assistance", label: "Assistance 24/7", icon: "🚗" },
                      { key: "vol", label: "Vol", icon: "🔐" },
                      { key: "vehiculeRemplacement", label: "Véhicule prêt", icon: "🔄" },
                    ].map((opt) => (
                      <button
                        key={opt.key}
                        onClick={() => setFormuleData({
                          ...formuleData,
                          options: { ...formuleData.options, [opt.key]: !formuleData.options[opt.key] }
                        })}
                        className={`p-3 rounded-xl border-2 flex items-center gap-2 transition-all ${
                          formuleData.options[opt.key]
                            ? "border-[#102692] bg-[#102692]/5"
                            : "border-gray-200"
                        }`}
                      >
                        <span className="text-xl">{opt.icon}</span>
                        <span className="text-sm font-medium">{opt.label}</span>
                        {formuleData.options[opt.key] && <Check className="w-4 h-4 text-[#102692] ml-auto" />}
                      </button>
                    ))}
                  </div>
                </div>

                {/* CGU */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="cgu"
                      checked={accepted}
                      onCheckedChange={setAccepted}
                    />
                    <Label htmlFor="cgu" className="text-xs text-gray-600 cursor-pointer leading-relaxed">
                      J'accepte les conditions générales et la politique de confidentialité. 
                      Je souhaite être contacté par JYVIS Assurances pour recevoir mon devis personnalisé.
                    </Label>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Navigation */}
          <div className="px-6 py-4 bg-gray-50 border-t flex justify-between items-center">
            <Button
              variant="ghost"
              onClick={prevStep}
              disabled={currentStep === 1}
              className="gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              Retour
            </Button>
            
            {currentStep < 6 ? (
              <Button onClick={nextStep} className="gap-2 bg-[#102692] hover:bg-[#0d1f75] px-8">
                Continuer
                <ChevronRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={loading || !accepted}
                className="gap-2 bg-[#F97316] hover:bg-[#EA580C] px-8"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Recevoir mon devis
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </Button>
            )}
          </div>
        </div>

        {/* WhatsApp CTA */}
        <div className="mt-6 text-center">
          <p className="text-gray-400 text-sm mb-2">Une question ?</p>
          <a 
            href={contactInfo.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#25D366] font-medium hover:underline"
          >
            Discuter sur WhatsApp
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </main>
      <SimpleLegalFooter />
    </div>
  );
}
